import { get, isString } from 'lodash';
import { isObject } from '../../common';
import { toFirestore } from '../../serialisation-provider';
import { toTimestamp } from '../../time/time';
import { isColRef } from '../firestore/collection';
import { SystemActors, CreatedBy } from '../interfaces';
import {
  type CollectionReference,
  type Query,
  type QueryConstraint,
  type Timestamp,
  type Transaction,
  type WriteBatch,
  collectionGroup,
  doc,
  query,
  runTransaction as runTransactionFirestore,
  store,
  writeBatch as writeBatchFirestore,
} from './adaptor';
import { asQueryRef } from './collection-group';
import { FirestoreScheduler } from './firestore-scheduler';

export function collectionGroupQuery<T>(
  collectionId: string,
  ...queryConstraints: QueryConstraint[]
): Query<T> {
  return query(
    asQueryRef<T>(
      collectionGroup(store(FirestoreScheduler.appName), collectionId)
    ) as unknown as Query<T>,
    ...queryConstraints
  );
}

export function writeBatch(): WriteBatch {
  return writeBatchFirestore(store(FirestoreScheduler.appName));
}

export async function addBulk<T extends object>(
  col: CollectionReference<T>,
  data: T[]
): Promise<void> {
  const batch: WriteBatch = writeBatch();
  const timestamp: Timestamp = toTimestamp();
  data.map((item: T) => {
    const serialisedData = toFirestore(item);
    const ref = doc(col);

    if (isObject(serialisedData) && !serialisedData.createdAt) {
      batch.set(ref, {
        ref,
        ...serialisedData,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return;
    }
    batch.set(ref, { ref, ...serialisedData, updatedAt: timestamp });
  });
  await batch.commit();
}

export async function upsertBulk<T extends object>(
  col: CollectionReference<T>,
  data: T[],
  uidPath: keyof T,
  updatedBy: CreatedBy = SystemActors.Unknown
): Promise<void> {
  const batch: WriteBatch = writeBatch();
  const timestamp: Timestamp = toTimestamp();
  data.map((item: T) => {
    const serialisedData = toFirestore(item);
    const ref = doc(col, String(get(item, uidPath)));

    if (isObject(serialisedData) && !serialisedData.createdAt) {
      batch.set(ref, {
        ref,
        ...serialisedData,
        createdAt: timestamp,
        updatedAt: timestamp,
        createdBy: updatedBy,
        updatedBy,
      });
      return;
    }
    batch.set(ref, {
      ref,
      createdBy: updatedBy,
      ...serialisedData,
      updatedAt: timestamp,
      updatedBy,
    });
  });
  await batch.commit();
}

export async function upsertBulkUniqueCol<T extends object>(
  data: IBulkUploadData<T>[]
): Promise<void> {
  const batch: WriteBatch = writeBatch();
  const timestamp: Timestamp = toTimestamp();
  data.map((item) => {
    const serialisedData = toFirestore(item.data);
    const ref = item.uid
      ? doc(item.collectionRef, item.uid)
      : doc(item.collectionRef);

    if (isObject(serialisedData) && !serialisedData.createdAt) {
      batch.set(ref, {
        ref,
        ...serialisedData,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return;
    }
    batch.set(ref, { ref, ...serialisedData, updatedAt: timestamp });
  });
  await batch.commit();
}

export interface IBulkUploadData<T> {
  collectionRef: CollectionReference<T>;
  data: T;
  uid?: string;
}

export function isBulkUploadData<T extends object = object>(
  item: unknown
): item is IBulkUploadData<T> {
  return (
    isObject(item) &&
    isString(item.uid) &&
    isColRef(item.collectionRef) &&
    isObject(item.data)
  );
}

export interface ITransactionOptions {
  maxAttempts: number;
}

/**
 * Raised transaction retry budget (SDK default is 5) for transactions on
 * contended documents. The function timeout bounds how many attempts actually
 * run; an attempt cut short by the timeout rolls back.
 */
export const TRANSACTION_MAX_ATTEMPTS = 10;

/**
 * @deprecated
 * Use Firestore.runTransaction instead
 */
export function runTransaction<T>(
  updateFunction: (transaction: Transaction) => Promise<T>,
  options?: ITransactionOptions
): Promise<T> {
  return runTransactionFirestore(
    store(FirestoreScheduler.appName),
    (transaction) => updateFunction(transaction as unknown as Transaction),
    options
  );
}

export async function retryWithBackoff(
  operation: () => Promise<void>,
  isError: (error: unknown) => boolean = () => true,
  maxAttempts: number = 3,
  delayMs: number = 50
): Promise<void> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await operation();
      return;
    } catch (error: unknown) {
      if (isError(error) && attempt < maxAttempts) {
        const baseDelay = Math.pow(2, attempt) * delayMs;
        const jitter = Math.random() * delayMs;
        const delay = baseDelay + jitter;
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
}
